import React, { useEffect, useMemo, useState } from 'react';
import {
    TextField,
    Button,
    Stack,
    Box,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    OutlinedInput,
    Chip,
    FormHelperText,
} from '@mui/material';
import { STATES, getDistrictsForState } from '../constants/regions';

/**
 * Form used for creating or updating a branch.
 */
const BranchForm = ({
    onSubmit,
    onCancel,
    businesses,
    contacts,
    initialData = null,
    defaultBusinessId = '',
    disableBusinessSelection = false,
    disableSupervisorSelection = false,
}) => {
    const [formData, setFormData] = useState({
        nickname: '',
        fssaiLicenseNumber: '',
        address: '',
        state: '',
        district: '',
        email: '',
        phoneNumber: '',
        businessId: '',
        supervisorIds: [],
        employeeIds: [],
    });
    const [errors, setErrors] = useState({});

    const isEditing = Boolean(initialData);

    const normalizePhoneNumber = (value) => {
        if (!value) {
            return { error: 'Phone number is required.' };
        }
        const trimmed = value.trim();
        if (trimmed.startsWith('+') && /^\+\d{10,14}$/.test(trimmed)) {
            if (trimmed.startsWith('+91') && trimmed.length === 13) {
                return { value: trimmed };
            }
            return { value: trimmed };
        }
        const digits = trimmed.replace(/\D/g, '');
        if (digits.length === 12 && digits.startsWith('91')) {
            return { value: `+${digits}` };
        }
        if (digits.length === 10) {
            return { value: `+91${digits}` };
        }
        return { error: 'Enter phone number with country code (e.g. +919876543210).' };
    };

    const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

    useEffect(() => {
        if (initialData) {
            setFormData({
                nickname: initialData.nickname || '',
                fssaiLicenseNumber: initialData.fssaiLicenseNumber || '',
                address: initialData.address || '',
                state: initialData.state || '',
                district: initialData.district || '',
                email: initialData.email || '',
                phoneNumber: initialData.phoneNumber || '',
                businessId: initialData.businessId || defaultBusinessId || '',
                supervisorIds: initialData.supervisorIds || [],
                employeeIds: initialData.employeeIds || [],
            });
        } else {
            setFormData(prev => ({
                ...prev,
                businessId: defaultBusinessId || '',
            }));
        }
    }, [initialData, defaultBusinessId]);

    const supervisorOptions = useMemo(() => {
        const supervisors = contacts.filter(contact => contact.role === 'Supervisor');
        const existingIds = new Set(supervisors.map(contact => contact.contactId));
        (initialData?.supervisorIds || []).forEach(id => {
            if (!existingIds.has(id)) {
                const contact = contacts.find(c => c.contactId === id);
                if (contact) supervisors.push(contact);
            }
        });
        return supervisors;
    }, [contacts, initialData]);

    const employeeOptions = useMemo(() => {
        const employees = contacts.filter(contact => contact.role === 'Employee');
        const existingIds = new Set(employees.map(contact => contact.contactId));
        (initialData?.employeeIds || []).forEach(id => {
            if (!existingIds.has(id)) {
                const contact = contacts.find(c => c.contactId === id);
                if (contact) employees.push(contact);
            }
        });
        return employees;
    }, [contacts, initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            if (name === 'state') {
                const districts = getDistrictsForState(value);
                const nextDistrict = districts.includes(prev.district) ? prev.district : '';
                return { ...prev, state: value, district: nextDistrict };
            }
            return { ...prev, [name]: value };
        });
    };

    const handleSupervisorChange = (event) => {
        const { value } = event.target;
        setFormData(prev => ({ ...prev, supervisorIds: typeof value === 'string' ? value.split(',') : value }));
    };

    const handleEmployeeChange = (event) => {
        const { value } = event.target;
        setFormData(prev => ({ ...prev, employeeIds: typeof value === 'string' ? value.split(',') : value }));
    };

    const districtOptions = useMemo(() => getDistrictsForState(formData.state), [formData.state]);

    const validate = () => {
        const nextErrors = {};
        let normalizedPhone = null;
        if (!formData.nickname) nextErrors.nickname = 'Nickname is required.';
        if (!formData.address) nextErrors.address = 'Address is required.';
        if (!formData.state) nextErrors.state = 'State is required.';
        if (!formData.district) {
            nextErrors.district = 'District is required.';
        } else if (!districtOptions.includes(formData.district)) {
            nextErrors.district = 'Select a district from the chosen state.';
        }
        if (!formData.phoneNumber) {
            nextErrors.phoneNumber = 'Phone number is required.';
        } else {
            const result = normalizePhoneNumber(formData.phoneNumber);
            if (result.error) nextErrors.phoneNumber = result.error;
            else normalizedPhone = result.value;
        }
        if (formData.email && formData.email.trim() && !isValidEmail(formData.email.trim())) {
            nextErrors.email = 'Enter a valid email address.';
        }
        if (!formData.businessId) nextErrors.businessId = 'Business is required.';
        setErrors(nextErrors);
        return {
            isValid: Object.keys(nextErrors).length === 0,
            normalizedPhone,
        };
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const { isValid, normalizedPhone } = validate();
        if (!isValid) return;

        const payload = {
            ...formData,
            phoneNumber: normalizedPhone || formData.phoneNumber,
            email: formData.email ? formData.email.trim() : '',
        };

        onSubmit(payload, initialData ? initialData.branchId : null);
    };

    const renderSupervisorChips = (selected) => selected
        .map(id => supervisorOptions.find(contact => contact.contactId === id)?.contactName || id)
        .join(', ');

    const renderEmployeeChips = (selected) => selected
        .map(id => employeeOptions.find(contact => contact.contactId === id)?.contactName || id)
        .join(', ');

    return (
        <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
                <TextField
                    label="Nickname"
                    name="nickname"
                    value={formData.nickname}
                    onChange={handleChange}
                    required
                    fullWidth
                    error={Boolean(errors.nickname)}
                    helperText={errors.nickname}
                />
                <TextField
                    label="FSSAI License Number"
                    name="fssaiLicenseNumber"
                    value={formData.fssaiLicenseNumber}
                    onChange={handleChange}
                    fullWidth
                />
                <TextField
                    label="Address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    fullWidth
                    error={Boolean(errors.address)}
                    helperText={errors.address}
                    multiline
                    minRows={2}
                />
                <FormControl fullWidth required error={Boolean(errors.state)}>
                    <InputLabel id="branch-state-select">State</InputLabel>
                    <Select
                        labelId="branch-state-select"
                        label="State"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                    >
                        {STATES.map(state => (
                            <MenuItem key={state} value={state}>{state}</MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>{errors.state || 'Select the branch state.'}</FormHelperText>
                </FormControl>
                <FormControl fullWidth required error={Boolean(errors.district)} disabled={!formData.state}>
                    <InputLabel id="branch-district-select">District</InputLabel>
                    <Select
                        labelId="branch-district-select"
                        label="District"
                        name="district"
                        value={formData.district}
                        onChange={handleChange}
                    >
                        {districtOptions.map(district => (
                            <MenuItem key={district} value={district}>{district}</MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        {formData.state ? (errors.district || 'Select the branch district.') : 'Choose a state first.'}
                    </FormHelperText>
                </FormControl>
                <TextField
                    label="Email Address (Optional)"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    fullWidth
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                />
                <TextField
                    label="Phone Number"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    required
                    fullWidth
                    error={Boolean(errors.phoneNumber)}
                    helperText={errors.phoneNumber}
                />

                <FormControl fullWidth required error={Boolean(errors.businessId)} disabled={disableBusinessSelection}>
                    <InputLabel id="branch-business-select">Business</InputLabel>
                    <Select
                        labelId="branch-business-select"
                        label="Business"
                        name="businessId"
                        value={formData.businessId}
                        onChange={handleChange}
                    >
                        {businesses.map(business => (
                            <MenuItem key={business.businessId} value={business.businessId}>
                                {business.businessName}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.businessId && <FormHelperText>{errors.businessId}</FormHelperText>}
                </FormControl>

                <FormControl fullWidth disabled={disableSupervisorSelection}>
                    <InputLabel id="branch-supervisors-label">Supervisors</InputLabel>
                    <Select
                        labelId="branch-supervisors-label"
                        multiple
                        value={formData.supervisorIds}
                        onChange={handleSupervisorChange}
                        input={<OutlinedInput label="Supervisors" />}
                        renderValue={renderSupervisorChips}
                    >
                        {supervisorOptions.map(contact => (
                            <MenuItem key={contact.contactId} value={contact.contactId}>
                                <Chip label={contact.contactName} sx={{ mr: 1 }} />
                                {contact.contactName}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>Select one or more supervisors for this branch.</FormHelperText>
                </FormControl>

                <FormControl fullWidth>
                    <InputLabel id="branch-employees-label">Employees</InputLabel>
                    <Select
                        labelId="branch-employees-label"
                        multiple
                        value={formData.employeeIds}
                        onChange={handleEmployeeChange}
                        input={<OutlinedInput label="Employees" />}
                        renderValue={renderEmployeeChips}
                    >
                        {employeeOptions.map(contact => (
                            <MenuItem key={contact.contactId} value={contact.contactId}>
                                <Chip label={contact.contactName} sx={{ mr: 1 }} />
                                {contact.contactName}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>Select employees assigned to this branch.</FormHelperText>
                </FormControl>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button type="button" variant="outlined" color="secondary" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" color="primary">
                        {isEditing ? 'Save Changes' : 'Create Branch'}
                    </Button>
                </Box>
            </Stack>
        </form>
    );
};

export default BranchForm;
